import { useEffect, useState, useCallback } from 'react'
import { useAdmin, type GlobalConnection } from '@/hooks/useAdmin'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PaginationControls } from '@/components/ui/pagination-controls'
import { Plug } from 'lucide-react'

const PAGE_SIZE = 50

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'connected', label: 'Connected' },
  { value: 'failed', label: 'Failed' },
  { value: 'pending', label: 'Pending' },
  { value: 'disconnected', label: 'Disconnected' },
]

function statusPillClass(status: string) {
  if (status === 'connected') return 'status-pill status-pill-success'
  if (status === 'failed') return 'status-pill status-pill-destructive'
  if (status === 'pending') return 'status-pill status-pill-amber'
  return 'status-pill status-pill-muted'
}

export function ConnectionsPage() {
  const { listConnections } = useAdmin()

  const [connections, setConnections] = useState<GlobalConnection[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('failed')

  const load = useCallback(
    (p: number, status: string) => {
      setLoading(true)
      setError(null)
      listConnections({
        status: status || undefined,
        limit: PAGE_SIZE,
        offset: (p - 1) * PAGE_SIZE,
      })
        .then(({ connections: c, total: t }) => {
          setConnections(c)
          setTotal(t)
        })
        .catch(() => setError('Failed to load connections'))
        .finally(() => setLoading(false))
    },
    [listConnections],
  )

  useEffect(() => {
    load(page, statusFilter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter])

  const handleStatusChange = (v: string) => {
    setStatusFilter(v)
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="space-y-6">
      <div>
        <h1 className="brand-wordmark text-2xl">Connections</h1>
        <p className="kicker text-muted-foreground mt-1">
          {total.toLocaleString()} connection{total !== 1 ? 's' : ''} across all organizations
        </p>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <Button
            key={f.value}
            variant={statusFilter === f.value ? 'default' : 'outline'}
            size="sm"
            className="h-8 text-xs"
            onClick={() => handleStatusChange(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Table */}
      <Card>
        {error ? (
          <CardContent className="p-6 text-sm text-destructive">{error}</CardContent>
        ) : loading ? (
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-6 py-3.5">
                  <div className="h-4 w-48 rounded bg-muted/40 animate-pulse" />
                  <div className="h-4 w-16 rounded bg-muted/30 animate-pulse" />
                  <div className="ml-auto h-4 w-24 rounded bg-muted/30 animate-pulse" />
                </div>
              ))}
            </div>
          </CardContent>
        ) : connections.length === 0 ? (
          <CardContent className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <Plug className="h-8 w-8 opacity-40" />
            <p className="text-sm">No connections found</p>
          </CardContent>
        ) : (
          <CardContent className="p-0">
            <div className="grid grid-cols-[1fr_120px_100px_70px_130px_130px_1fr] gap-3 px-6 py-2.5 border-b text-xs font-medium text-muted-foreground uppercase tracking-wide">
              <span>Org / Label</span>
              <span>Status</span>
              <span>Auth type</span>
              <span className="text-center">Sandbox</span>
              <span>Last connected</span>
              <span>SF Org ID</span>
              <span>Instance URL</span>
            </div>
            <div className="divide-y divide-border">
              {connections.map((conn) => (
                <div
                  key={conn.id}
                  className="grid grid-cols-[1fr_120px_100px_70px_130px_130px_1fr] gap-3 px-6 py-3 items-center text-sm hover:bg-accent/20 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{conn.org_name || conn.organization_id}</p>
                    <p className="text-xs text-muted-foreground truncate">{conn.label}</p>
                  </div>
                  <div>
                    <span className={statusPillClass(conn.status)}>{conn.status}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{conn.auth_type || '—'}</p>
                  <div className="flex justify-center">
                    {conn.is_sandbox ? (
                      <Badge variant="outline" className="text-xs text-muted-foreground">Sandbox</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground/40">—</span>
                    )}
                  </div>
                  <p className="mono-accent text-muted-foreground text-xs">
                    {conn.last_connected_at
                      ? new Date(conn.last_connected_at).toLocaleDateString()
                      : '—'}
                  </p>
                  <p className="mono-accent text-muted-foreground text-xs truncate">{conn.sf_org_id || '—'}</p>
                  <p className="mono-accent text-muted-foreground text-xs truncate">{conn.instance_url || '—'}</p>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {!loading && !error && total > 0 && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      )}
    </div>
  )
}
